import React, { createContext, useEffect, useReducer } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { App } from '@roku-web-core/app';

import { logAspenEvent } from 'modules/aspenLogger';
import { getAppFeatures } from 'modules/app';
import { registerCleanup } from 'modules/globalCleanup';

import { AppFeatures } from 'types/app';

interface AppContext {
  appFeatures: AppFeatures;
  appSessionId: string;
  deviceOrder: Array<string>;
  eventsFilterOpen: boolean;
  microphonePermissionState: PermissionState;
  userId: string;
}

type AppActions =
  | {
      type: 'deviceOrder';
      payload: AppContext['deviceOrder'];
    }
  | {
      type: 'eventsFilterOpen';
      payload: AppContext['eventsFilterOpen'];
    }
  | {
      type: 'microphonePermissionState';
      payload: AppContext['microphonePermissionState'];
    }
  | {
      type: 'userId';
      payload: AppContext['userId'];
    };

export const AppContext = createContext<AppContext>({} as AppContext);

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const AppDispatchContext = createContext<React.Dispatch<AppActions>>(() => {});

interface AppProviderProps {
  initState?: AppContext;
  children: React.ReactNode;
}

const reducer = (state: AppContext, action: AppActions) => {
  switch (action.type) {
    case 'deviceOrder':
      return { ...state, deviceOrder: action.payload };
    case 'eventsFilterOpen':
      return { ...state, eventsFilterOpen: action.payload };
    case 'microphonePermissionState':
      return { ...state, microphonePermissionState: action.payload };
    case 'userId':
      return { ...state, userId: action.payload };
    default:
      return state;
  }
};

const DEFAULT_INIT_STATE: AppContext = {
  appFeatures: {
    events: false,
    lights: false,
  },
  appSessionId: '',
  deviceOrder: [],
  eventsFilterOpen: false,
  microphonePermissionState: 'prompt',
  userId: '',
};

export const AppProvider = ({ initState = DEFAULT_INIT_STATE, children }: AppProviderProps) => {
  const { pageviewPrereqData } = App.getPageConfig();
  const { features, endpoints } = App.getResource();
  const appFeatures = getAppFeatures(features);

  const appSessionId = pageviewPrereqData?.appSessionId || uuidv4();
  localStorage.setItem('appSessionId', appSessionId);
  const userId = pageviewPrereqData?.userId;

  const aspenLogsEndpoint = endpoints?.aspen_logs;

  const logAspenEventWithEndpoint =
    aspenLogsEndpoint && userId
      ? logAspenEvent(aspenLogsEndpoint, {
          account_id: userId,
          web_session_id: appSessionId,
        })
      : null;

  const [state, dispatch] = useReducer(reducer, { ...initState, appFeatures, userId, appSessionId });
  const [hasLoggedLoad, setHasLoggedLoad] = React.useState(false);

  useEffect(() => {
    // Log iot.web.load BDP event only once
    if (logAspenEventWithEndpoint && !hasLoggedLoad) {
      logAspenEventWithEndpoint({
        log_id: 'iot.web.load',
        payload: {
          referrer: document.referrer,
          user_agent: navigator.userAgent,
        },
      });
      setHasLoggedLoad(true);
    }
  }, [logAspenEventWithEndpoint, hasLoggedLoad]);

  // Register global cleanup for analytics logging
  useEffect(() => {
    registerCleanup('app-analytics', () => {
      // Log iot.web.unload BDP event
      if (logAspenEventWithEndpoint) {
        logAspenEventWithEndpoint({
          log_id: 'iot.web.unload',
          payload: {},
        });
      }
    });
  }, []);

  useEffect(() => {
    const getMicPermission = async () => {
      const micPermissionStatus = await navigator.permissions.query({ name: 'microphone' });
      dispatch({ type: 'microphonePermissionState', payload: micPermissionStatus.state });

      micPermissionStatus.onchange = () => {
        dispatch({ type: 'microphonePermissionState', payload: micPermissionStatus.state });
      };
    };

    getMicPermission();
  }, [navigator.permissions]);

  return (
    <AppContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>{children}</AppDispatchContext.Provider>
    </AppContext.Provider>
  );
};
